// Readout service — the centralized per-system readout (GET /admin/readout*).
//
// Types mirror services/readout/models.py. Money arrives as numbers (already
// folded server-side: cost = live AI spend + ledger cost lines; net = benefit -
// cost); the page only formats. "No value recorded" is an explicit server-side
// state: net_usd is null (and benefit_lines / systems_with_value is 0) until a
// benefit line exists, and the page says "value not recorded yet".

import api from './api';

export interface ReadoutSystem {
  id: string;
  key: string;
  name: string;
  category: string;
  status: string;
  description: string | null;
  owner: string | null;
  sort_order: number;
  surfaces: Record<string, unknown>;
  recorded_by: string;
  updated_at: string;
}

export interface LiveMetric {
  metric_key: string;
  label: string;
  value: number | null;
  text_value: string | null;
  unit: string;
  detail: string | null;
}

export interface LiveExample {
  key: string;
  title: string;
  subtitle: string | null;
  when: string | null;
  /** Why this row deserves a look (e.g. "a person replied on Oct 12"); null = nothing to see. */
  flag: string | null;
}

export interface LiveSection {
  reader: string;
  status: 'ok' | 'unavailable' | 'empty';
  metrics: LiveMetric[];
  error: string | null;
  examples_label?: string | null;
  examples?: LiveExample[];
}

export interface MetricPoint {
  as_of: string;
  period_start: string | null;
  period_end: string | null;
  value: number | null;
  text_value: string | null;
  source: string;
  recorded_by: string;
}

export interface MetricSeries {
  metric_key: string;
  label: string;
  unit: string;
  latest: MetricPoint;
  points: MetricPoint[];
}

export interface LedgerEntry {
  id: string;
  entry_kind: 'cost' | 'funding' | 'benefit' | 'risk' | string;
  category: string;
  headline: string;
  detail: string | null;
  amount_usd: number | null;
  quantity: number | null;
  quantity_unit: string | null;
  period_start: string | null;
  period_end: string | null;
  status: 'estimated' | 'verified' | 'rejected' | string;
  confidence: string;
  source: string;
  recorded_by: string;
  evidence: Record<string, unknown>;
  updated_at: string;
}

export interface Narrative {
  id: string;
  as_of: string;
  headline: string;
  body_md: string;
  highlights: string[];
  risks: string[];
  next_steps: string[];
  recorded_by: string;
  references: Record<string, unknown>;
}

export interface MoneySummary {
  cost_usd: number;
  funding_usd: number;
  benefit_usd: number;
  /** Non-rejected benefit lines in the window; 0 = value not recorded yet. */
  benefit_lines: number;
  /** benefit - cost, or null until a benefit line is recorded. */
  net_usd: number | null;
  ai_spend_usd: number;
  ledger_cost_usd: number;
  verified_benefit_usd: number;
  estimated_benefit_usd: number;
  open_risks: number;
}

export interface SystemCard {
  system: ReadoutSystem;
  narrative_headline: string | null;
  narrative_as_of: string | null;
  narrative_age_days: number | null;
  money: MoneySummary;
  headline_metrics: LiveMetric[];
  live_status: 'ok' | 'partial' | 'unavailable' | 'none';
}

export interface ReadoutTotals {
  systems: number;
  active_systems: number;
  cost_usd: number;
  funding_usd: number;
  benefit_usd: number;
  /** Systems with at least one benefit line in the window. */
  systems_with_value: number;
  /** Benefit - cost over the systems with a value; null when none has one. */
  net_usd: number | null;
  ai_spend_usd: number;
  unattributed_ai_spend_usd: number;
  narratives_fresh: number;
  narratives_stale: number;
  narratives_missing: number;
}

export interface ReadoutOverview {
  as_of: string;
  window_days: number;
  window_start: string;
  stale_after_days: number;
  totals: ReadoutTotals;
  systems: SystemCard[];
}

export interface SystemReadout {
  as_of: string;
  window_days: number;
  window_start: string;
  system: ReadoutSystem;
  narrative: Narrative | null;
  narrative_history: Narrative[];
  money: MoneySummary;
  live: LiveSection[];
  metrics: MetricSeries[];
  ledger: LedgerEntry[];
}

export interface MetricSourceDoc {
  metric_key: string;
  label: string;
  source: string;
  calculation: string;
  window: 'window' | 'now' | 'none' | string;
  caveats: string | null;
}

export interface ReaderSourceDoc {
  reader: string;
  label: string;
  description: string;
  systems: string[];
  metrics: MetricSourceDoc[];
}

export interface RuleDoc {
  name: string;
  rule: string;
}

export interface RecordedSourceDoc {
  table: string;
  what: string;
  written_by: string;
  shown_as: string;
}

export interface SourceCatalog {
  window_note: string;
  attribution: RuleDoc[];
  readers: ReaderSourceDoc[];
  money_rules: RuleDoc[];
  recorded: RecordedSourceDoc[];
  freshness: RuleDoc[];
}

export const readoutService = {
  getSources: async (): Promise<SourceCatalog> => {
    const response = await api.get<SourceCatalog>('/admin/readout/sources');
    return response.data;
  },
  getOverview: async (days: number): Promise<ReadoutOverview> => {
    const response = await api.get<ReadoutOverview>(`/admin/readout?days=${days}`);
    return response.data;
  },
  getSystem: async (key: string, days: number): Promise<SystemReadout> => {
    const response = await api.get<SystemReadout>(`/admin/readout/systems/${encodeURIComponent(key)}?days=${days}`);
    return response.data;
  },
};
